import { createSelector } from '@reduxjs/toolkit';

import { PackageProductSubtype } from 'types/enums/PackageProductSubtype';
import { CreditFeatureType } from 'types/interfaces/Credits';
import { Package } from 'types/interfaces/Package';

import { PackagesThemeGroups } from 'api/AuthApi';
import { isNotEmptyArray } from 'helpers/utils/isNotEmptyArray';
import { RootState } from 'store/rootReducer';

const isTrialPackage = (pack: Package) => pack?.is_trial;
const findPackById = (packages: Package[], id: string) =>
  packages.find((pack) => pack.id === id);

const getPaymentState = (state: RootState) => state.payment;
export const getUpgradePackages = (state: RootState) =>
  state.payment.upgradePackages;
export const getStopExitPackages = createSelector(
  getPaymentState,
  (payment) => payment.stopExitPackage
);
export const getLifetimePackage = createSelector(
  getPaymentState,
  (payment) => payment.lifetimePackage[0]
);

export const getAllUpgradePackages = createSelector(
  getUpgradePackages,
  getStopExitPackages,
  (upgradePackages, stopExitPackages) => [
    ...upgradePackages,
    ...stopExitPackages,
  ]
);
export const getTrialPackage = createSelector(
  getAllUpgradePackages,
  (packages) => packages.find(isTrialPackage)
);

export const getUpgradePackageByIdSelector = (id: string) =>
  createSelector(getAllUpgradePackages, (packages) =>
    findPackById(packages, id)
  );

export const getPreferPackageId = createSelector(
  getTrialPackage,
  getUpgradePackages,
  (trialPackage, upgradePackages) => trialPackage?.id || upgradePackages[0]?.id
);

export const getUpgradePackageCampaignId = createSelector(
  getUpgradePackages,
  (upgradePackages) =>
    upgradePackages.find((pack) => pack.campaign_id)?.campaign_id
);

export const getUpsalePackages = (state: RootState) =>
  state.payment.upsalePackages;

export const getActivePackage = (state: RootState) =>
  state.payment.upgradePackages.find((pack) => pack.active) ?? null;

export const getPackagesTheme = (state: RootState) => state.payment.theme;

export const isFreeTrialPackageThemeSelector = (state: RootState) => {
  return state.payment.theme === PackagesThemeGroups.FreeTrial;
};

export const getVipPackages = (state: RootState) => state.payment.vipPackages;

export const getVipPackageId = createSelector(getVipPackages, (packages) => {
  if (!packages[0]) {
    return undefined;
  }

  return packages[0].id;
});

export const getMicroPackages = (state: RootState) =>
  state.payment.microPackages;

export const getVipDiscountAvailable = (state: RootState) =>
  state.payment.vipDiscountAvailable;

export const getCreditPackagesSelector = (state: RootState) =>
  state.payment.creditsPackages;

export const getCreditBalanceSelector = (state: RootState) =>
  state.payment.credits.amount;

export const getCreditsCurrencySelector = (state: RootState) =>
  state.payment.credits.currency;

export const getCreditByFeatureType = (type: CreditFeatureType) =>
  createSelector(
    getPaymentState,
    (payment) =>
      payment.credits.credit_features.find((feature) => feature.type === type)
        ?.credits
  );

export const getVipPaymentSuccess = (state: RootState) =>
  state.payment.vipPaymentSuccess;

export const getDefaultCreditPackageSelector = createSelector(
  getCreditPackagesSelector,
  (packages) => packages.find((pack) => pack.is_default)
);

export const getHasVipPackages = createSelector(
  getVipPackages,
  isNotEmptyArray
);

export const getBoostPackage = createSelector(getMicroPackages, (packs) =>
  packs.find((pack) => pack.product_subtype === PackageProductSubtype.Boost)
);

export const getCreditPackageByIdSelector = (id: string) =>
  createSelector(getCreditPackagesSelector, (packages) =>
    packages.find((pack) => pack.id === id)
  );

export const getOptimalCreditPackageSelector = (
  balance?: number,
  cost?: number
) => {
  return createSelector(getCreditPackagesSelector, (packages) => {
    if (typeof balance === 'number' && typeof cost === 'number') {
      const sortedPackages = [...packages].sort(
        (a, b) => a.credits_buy - b.credits_buy
      );

      return sortedPackages.find((pack) => pack.credits_buy + balance >= cost);
    }

    return packages.find((pack) => pack.is_default);
  });
};

export const getIsDowngradeAvailable = (state: RootState) => {
  return state.payment.downgradeAvailable;
};
